import { useOutlineSettingsAutoSave } from "./useOutlineSettingsAutoSave";
import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useOnScreen } from "src/hooks/useOnScreen";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../../AppEventPublisher";
import Auth from "../../../Auth";
import { useRefetchOnFocus } from "../../../hooks/useRefetchOnFocus";
import { useAutoSave } from "../../../hooks/useAutoSave";
import { useSeriesStore } from "../../../stores/seriesStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { useToast } from "../../ToastWrapper";
import CreateDocument, { CREATE_DOCUMENT_DISPLAY_MODES } from "./CreateDocument";
import Header from "./Header";
import OutlineBody from "./OutlineBody";
import RewriteOutline from "./RewriteOutline";
import { useStructuredOutlineStore } from "./StructuredOutlineStore";

function StructuredOutline(props) {
  const { projectId, project, setDocumentId, documentId, collapsed = false, onToggleCollapse } = props;
  // localOutline services as a pristine version of the server side
  // if we differ locally, then we need to update the server
  // this prevents us from getting into update loops.
  const [localOutline, setLocalOutline] = useState(null);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const setOutline = useStructuredOutlineStore((state) => state.setOutline);
  const localDirty = useStructuredOutlineStore((state) => state.localDirty);
  const setLocalClean = useStructuredOutlineStore((state) => state.setLocalClean);
  const readOnly = useStructuredOutlineStore((state) => state.readOnly);
  const reorderItem = useStructuredOutlineStore((state) => state.reorderItem);
  const setRestoring = useStructuredOutlineStore((state) => state.setRestoring);
  const restoring = useStructuredOutlineStore((state) => state.restoring);
  const setIsLoading = useStructuredOutlineStore((state) => state.setIsLoading);
  const setAllScenes = useSeriesStore((state) => state.setAllScenes);
  const { setToast } = useToast();
  const rewriteOutlineTextAreaRef = useRef(null);
  const forceRenderSearchables = useStoryBibleStore((state) => state.forceRenderSearchables);
  const [syncing, setSyncing] = useState(false); // This is meant for external syncs that happen when you focus into the editor
  const wrapperRef = useRef(null);
  const isVisibleOnScreen = useOnScreen(wrapperRef, "300px");
  const isVisible = isVisibleOnScreen || forceRenderSearchables;
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const showGoToChat = outline?.content?.showGoToChat ?? false;
  const showRewriteOutlinePanel =
    outline.content?.rewriteOpen
    && (!hasAccessToAgenticChat || showGoToChat);

  const loadOutline = useCallback(
    async (external) => {
      try {
        if (external) {
          setSyncing(true);
        }
        setIsLoading(true);
        const { outline } = await Auth.fetchWithIdToken(`/api/outline`, { projectId });
        setOutline(outline, { refreshPOVTense: true });
        setLocalOutline(outline);
        setLocalClean();
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "structured_outline_load" } });
        setToast("Could not load outline");
      } finally {
        setIsLoading(false);
        if (external) {
          setSyncing(false);
        }
      }
    },
    [projectId]
  );

  // for editing outlines from other projects
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);

  const saveOutline = useCallback(
    async (outlineToSave) => {
      if (restoring) return;

      try {
        const result = await Auth.fetchWithIdToken(`/api/outline`, {
          ...outlineToSave, // projectId is part of outline to facilitate editing other outlines
          method: "PUT",
        });

        if (result.success) {
          // Check if newer local changes arrived while this save was in-flight.
          // If so, don't overwrite the store's outline (which would cause a visual
          // "reappear" of deleted items) — just update localOutline so the effect
          // knows what the server has, and let the pending debounced save resolve it.
          const currentOutline = useStructuredOutlineStore.getState().outline;
          const hasNewerChanges = !isEqual(currentOutline?.content, outlineToSave?.content);

          if (hasNewerChanges) {
            setLocalOutline(result.outline);
          } else {
            setOutline(result.outline);
            setLocalOutline(result.outline);
            setLocalClean();
          }
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: "structured_outline_save" } });
        setToast("Could not save outline");
      }
    },
    [restoring, setToast, setOutline, setLocalOutline, setLocalClean]
  );

  const pendingSettings = useStructuredOutlineStore((state) => state.pendingSettings);
  const { handleBlur: handleSettingsBlur, withSettingsRestore } = useOutlineSettingsAutoSave({
    pendingSettings, readOnly, restoring, setToast,
  });

  // Snapshot creation function
  const createOutlineSnapshot = useCallback(async () => {
    if (!outline?.id || !localDirty) return; // Only create snapshot if there are changes

    try {
      await Auth.fetchWithIdToken(`/api/outline/${outline.id}/snapshot`, {
        method: "POST",
        projectId: outline.projectId || projectId,
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "structured_outline_snapshot" } });
    }
  }, [outline, localDirty, projectId]);

  // Use shared auto-save hook with blur/visibility handling and snapshot creation
  const { handleBlur: handleContentBlur, debouncedSave: updateOutline } = useAutoSave({
    saveFn: saveOutline,
    entity: outline,
    createSnapshot: createOutlineSnapshot,
    readOnly,
  });

  const handleOutlineFieldBlur = useCallback(async () => {
    await Promise.all([handleContentBlur(), handleSettingsBlur()]);
  }, [handleContentBlur, handleSettingsBlur]);

  const restoreOutline = async (snapshot) => {
    try {
      setRestoring(true);
      const result = await withSettingsRestore(outline.projectId, () =>
        Auth.fetchWithIdToken(`/api/outline/${outline.id}/restoreSnapshot`, {
          projectId: outline.projectId,
          snapshotId: snapshot.id,
        }),
      );
      setToast("Outline Saved");
      setOutline(result.outline);
      setLocalOutline(result.outline);
      setLocalClean();
      useStructuredOutlineStore.getState().clearAllChapterDiffs();
      AppEventPublisher.storyBible.edited(StoryBibleType.Outline, StoryBibleAction.RestoreHistory);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "structured_outline_restore" } });
      setToast("There was a problem restoring this snapshot. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  useRefetchOnFocus(loadOutline, [loadOutline], { refetchOnBlur: false });

  useEffect(() => {
    if (!localOutline) return;
    if (isEqual(localOutline, outline)) return;
    if (!localDirty) return;
    updateOutline(outline);
  }, [localOutline, outline, updateOutline, localDirty]);

  useEffect(() => {
    if (outline?.content?.items) {
      const scenes = outline.content.items
        .filter((item) => item.type === "chapter")
        .map((item) => ({
          id: item.id,
          itemId: item.id,
          name: item.name,
          summary: item.summary,
        }));
      setAllScenes(outline.projectId, scenes);
    }
  }, [outline, projectId, setAllScenes]);

  // Keep the visibility observer mounted, but expose settings only once owned.
  if (!outline.projectId) return <div ref={wrapperRef} />;

  return (
    <div ref={wrapperRef}>
      <Header
        projectId={projectId}
        onGenerate={() => {
          rewriteOutlineTextAreaRef.current?.focus();
        }}
        restoreOutline={restoreOutline}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
      />
      {!collapsed && (
        <>
          {showRewriteOutlinePanel && (
            <RewriteOutline
              projectId={projectId}
              rewriteOutlineTextAreaRef={rewriteOutlineTextAreaRef}
            />
          )}
          {isVisible ? (
            <OutlineBody
              readOnly={readOnly}
              outline={outline}
              outlineProjectId={outline?.projectId}
              reorderItem={reorderItem}
              projectId={projectId}
              syncing={syncing}
              onFieldBlur={handleOutlineFieldBlur}
            />
          ) : null}
          <CreateDocument
            project={project}
            documentId={documentId}
            setDocumentId={setDocumentId}
            mode={CREATE_DOCUMENT_DISPLAY_MODES.followsOutline}
          />
        </>
      )}
    </div>
  );
}

export default memo(StructuredOutline);
